import React from 'react';
import LeadGoalProgress from './LeadGoalProgress';
import { splitCommonPrefix } from '../../utils/splitCommonPrefix';

const MONTH_NAMES = ['janeiro', 'fevereiro', 'março', 'abril', 'maio', 'junho', 'julho', 'agosto', 'setembro', 'outubro', 'novembro', 'dezembro'];
const MONTH_SHORT = ['jan', 'fev', 'mar', 'abr', 'mai', 'jun', 'jul', 'ago', 'set', 'out', 'nov', 'dez'];

const TYPE_OPTIONS = [
  { value: 'message', label: 'Conversas' },
  { value: 'lead', label: 'Leads' }
];
const TYPE_LABEL = { message: 'Conversas Iniciadas', lead: 'Leads Cadastros' };
const MAX_SKELETONS = 12;

// Evita new Date('YYYY-MM-DD') (UTC) -> dia anterior em BRT.
function parseYmd(s) {
  const [y, m, d] = String(s || '').slice(0, 10).split('-').map(n => parseInt(n, 10));
  return { y, m, d };
}

function buildLegend(data, readOnly) {
  const { y, m, d } = parseYmd(data.asOf);
  if (!y || !m) return '';
  const base = `Mês de ${MONTH_NAMES[m - 1]}/${y} (1-${d} ${MONTH_SHORT[m - 1]}), independente do período selecionado · Realizado: Meta Ads`;
  return readOnly ? `${base} · posição em ${String(d).padStart(2, '0')}/${String(m).padStart(2, '0')}/${y}` : base;
}

// Bloco "METAS DO MES" do relatorio combinado. Em readOnly (link compartilhado) o ritmo
// vem do asOf congelado no snapshot, nunca do "hoje" de quem visualiza.
export default function LeadGoalsBlock({
  data,
  status = 'ready', // 'loading' | 'ready' | 'error'
  type = 'message',
  onTypeChange,
  onRetry,
  readOnly = false,
  unmappedCount = 0,
  incompleteCount = 0, // contas selecionadas de unidades parcialmente marcadas (sem card)
  expectedUnits = 0, // unidades pedidas (dimensiona o skeleton)
  overLimitCount = 0 // unidades alem do maximo por consulta
}) {
  const typeLabel = TYPE_LABEL[type] || TYPE_LABEL.message;
  const units = data?.units || [];
  const hasUnits = units.length > 0;

  if (status === 'ready' && !hasUnits && unmappedCount === 0 && incompleteCount === 0) return null;

  const { prefix, names } = splitCommonPrefix(units.map(u => u.unitName));

  const goalFor = (u, i) => ({
    unitName: names[i],
    plannedLeads: u.plannedLeads,
    referenceMonth: data.referenceMonth,
    currentDay: data.daysElapsed,
    totalDaysInMonth: data.daysInMonth,
    expectedPacingPct: u.expectedPct
  });

  const totals = data?.totals;
  const expectedPct = hasUnits ? units[0].expectedPct : 0;
  const showSummary = status === 'ready' && totals && totals.unitsWithGoal > 0;
  const partialCount = units.filter(u => u.state === 'partial').length;
  const skeletons = Math.min(Math.max(expectedUnits, 1), MAX_SKELETONS);

  let liveText = '';
  if (status === 'loading') liveText = 'Carregando metas do mês';
  else if (status === 'error') liveText = 'Metas indisponíveis no momento';
  else if (totals) liveText = `Metas carregadas: ${totals.unitsWithGoal} de ${totals.unitsTotal} unidades com meta`;

  return (
    <section aria-labelledby="metas-do-mes-title" className="space-y-4 break-inside-avoid">
      <div className="flex flex-wrap items-center gap-3">
        <h3 id="metas-do-mes-title" className="font-headline text-sm font-bold text-slate-700 uppercase tracking-widest flex items-center gap-2">
          <span className="material-symbols-outlined text-primary text-[20px]" aria-hidden="true">flag</span>
          Metas do mês · {typeLabel}{prefix ? ` · ${prefix}` : ''}
        </h3>
        <span className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-xs font-bold bg-blue-100 text-blue-800">
          <span className="w-1.5 h-1.5 rounded-full bg-blue-600 inline-block" aria-hidden="true"></span>
          Meta Ads
        </span>

        {!readOnly && (
          <div role="group" aria-label="Meta de" data-html2canvas-ignore="true" className="print:hidden ml-auto flex items-center gap-2">
            <span className="text-xs font-bold text-on-surface-variant" aria-hidden="true">Meta de:</span>
            <div className="flex items-center gap-1 bg-slate-50 p-1 rounded-xl">
              {TYPE_OPTIONS.map(o => (
                <button
                  key={o.value}
                  type="button"
                  aria-pressed={type === o.value}
                  onClick={() => onTypeChange && onTypeChange(o.value)}
                  className={`px-3 py-1.5 rounded-lg text-xs uppercase tracking-wider font-bold transition-all ${type === o.value ? 'bg-white shadow-sm text-slate-800' : 'text-slate-600 hover:text-slate-800'}`}
                >
                  {o.label}
                </button>
              ))}
            </div>
          </div>
        )}
      </div>

      {/* Anuncio curto para leitores de tela (nao o container inteiro) */}
      <p className="sr-only" aria-live="polite">{liveText}</p>

      <div>
        {status === 'loading' && (
          <div role="status" aria-busy="true" aria-label="Carregando metas do mês" className="space-y-4">
            <div aria-hidden="true" className="bg-surface-container-lowest p-5 rounded-2xl border border-outline-variant/20 shadow-sm animate-pulse motion-reduce:animate-none">
              <div className="h-4 bg-surface-container-high rounded w-48 mb-3" />
              <div className="h-7 bg-surface-container-high rounded w-36 mb-3" />
              <div className="h-3 bg-surface-container-high rounded w-full" />
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
              {Array.from({ length: skeletons }, (_, i) => (
                <LeadGoalProgress key={i} variant="compact" conversionType={type} state="loading" goal={{}} />
              ))}
            </div>
          </div>
        )}

        {status === 'error' && (
          <div className="bg-surface-container-low p-4 rounded-xl border border-outline-variant/40 text-xs text-on-surface flex items-center gap-3">
            <span className="material-symbols-outlined text-[16px] text-on-surface-variant" aria-hidden="true">info</span>
            <span className="font-medium">Metas indisponíveis no momento.</span>
            {!readOnly && onRetry && (
              <button type="button" onClick={onRetry} data-html2canvas-ignore="true" className="print:hidden ml-auto text-xs font-bold text-on-primary-fixed-variant hover:underline">
                Tentar novamente
              </button>
            )}
          </div>
        )}

        {status === 'ready' && hasUnits && (
          <div className="space-y-4">
            {showSummary && (
              <div className="space-y-2">
                <LeadGoalProgress
                  variant="summary"
                  conversionType={type}
                  metricLabel={typeLabel}
                  sourceLabel="Meta Ads"
                  state="ok"
                  achieved={totals.achieved}
                  legend={buildLegend(data, readOnly)}
                  goal={{
                    unitName: `${totals.unitsWithGoal} de ${totals.unitsTotal} unidades com meta`,
                    plannedLeads: totals.plannedLeads,
                    referenceMonth: data.referenceMonth,
                    currentDay: data.daysElapsed,
                    totalDaysInMonth: data.daysInMonth,
                    expectedPacingPct: expectedPct
                  }}
                />
                {partialCount > 0 && (
                  <p className="text-xs text-amber-900 font-medium">
                    Inclui {partialCount} {partialCount === 1 ? 'unidade' : 'unidades'} com dados parciais: o realizado pode estar subestimado.
                  </p>
                )}
              </div>
            )}
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
              {units.map((u, i) => (
                <LeadGoalProgress
                  key={u.unitId}
                  variant="compact"
                  conversionType={type}
                  state={u.state}
                  goal={goalFor(u, i)}
                  achieved={u.achieved || 0}
                  accountsOk={u.accountsOk}
                  accountsFailed={u.accountsFailed}
                  ariaLabel={u.state === 'ok' || u.state === 'partial'
                    ? `${u.unitName}: ${(u.achieved || 0).toLocaleString('pt-BR')} de ${(u.plannedLeads || 0).toLocaleString('pt-BR')} ${typeLabel.toLowerCase()} (${(u.pct ?? 0).toLocaleString('pt-BR', { minimumFractionDigits: 1, maximumFractionDigits: 1 })}%)${u.state === 'partial' ? ', dados parciais' : ''}`
                    : undefined}
                />
              ))}
            </div>
            {!showSummary && (
              <p className="text-xs text-on-surface">{buildLegend(data, readOnly)}</p>
            )}
          </div>
        )}
      </div>

      {overLimitCount > 0 && (
        <p className="text-xs text-amber-900 bg-amber-50 border border-amber-200 rounded-xl px-3 py-2 flex items-center gap-2">
          <span className="material-symbols-outlined text-[16px]" aria-hidden="true">warning</span>
          Metas exibidas para as primeiras 50 unidades; {overLimitCount} {overLimitCount === 1 ? 'unidade ficou' : 'unidades ficaram'} de fora. Reduza a seleção para ver todas.
        </p>
      )}

      {incompleteCount > 0 && (
        <p className="text-xs text-amber-900 bg-amber-50 border border-amber-200 rounded-xl px-3 py-2 flex items-center gap-2">
          <span className="material-symbols-outlined text-[16px]" aria-hidden="true">warning</span>
          {incompleteCount} {incompleteCount === 1 ? 'conta pertence' : 'contas pertencem'} a unidades incompletas: marque a unidade inteira para ver a meta.
        </p>
      )}

      {unmappedCount > 0 && (
        <p className="text-xs text-amber-900 bg-amber-50 border border-amber-200 rounded-xl px-3 py-2 flex items-center gap-2">
          <span className="material-symbols-outlined text-[16px]" aria-hidden="true">warning</span>
          {unmappedCount} {unmappedCount === 1 ? 'conta sem unidade mapeada' : 'contas sem unidade mapeada'} (sem card de meta).
        </p>
      )}
    </section>
  );
}
